export type AnalyticsConsentStatus = 'unset' | 'accepted' | 'rejected'

export interface AnalyticsConsentRecord {
  status: AnalyticsConsentStatus
  updatedAt: string
  version: '1'
}

const CONSENT_STORAGE_KEY = 'neeno.analytics-consent'
const CONSENT_VERSION: AnalyticsConsentRecord['version'] = '1'

const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
let analyticsInitialized = false

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

function canLoadAnalytics() {
  return import.meta.env.PROD && Boolean(gaMeasurementId)
}

function readRecord(): AnalyticsConsentRecord | null {
  const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as AnalyticsConsentRecord
    if (!parsed.status || !parsed.updatedAt || !parsed.version) return null
    return parsed
  } catch {
    return null
  }
}

function writeRecord(status: AnalyticsConsentStatus) {
  const record: AnalyticsConsentRecord = {
    status,
    updatedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  }

  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record))
  window.dispatchEvent(
    new CustomEvent<AnalyticsConsentRecord>('neeno-consent-changed', { detail: record }),
  )
}

function ensureAnalyticsInitialized() {
  if (!canLoadAnalytics() || analyticsInitialized || !gaMeasurementId) return

  window.dataLayer = window.dataLayer || []
  const gtag = (...args: unknown[]) => {
    window.dataLayer.push(args)
  }

  window.gtag = gtag

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`
  document.head.appendChild(script)

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  })
  gtag('js', new Date())
  gtag('config', gaMeasurementId, { anonymize_ip: true })

  analyticsInitialized = true
}

function setGoogleConsent(mode: 'accepted' | 'rejected') {
  if (!gaMeasurementId) return

  const disableKey = `ga-disable-${gaMeasurementId}`
  ;(window as unknown as Record<string, unknown>)[disableKey] = mode === 'rejected'

  if (!window.gtag) return

  window.gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: mode === 'accepted' ? 'granted' : 'denied',
  })
}

export function getAnalyticsConsentStatus(): AnalyticsConsentStatus {
  return readRecord()?.status ?? 'unset'
}

export function acceptAnalyticsConsent() {
  ensureAnalyticsInitialized()
  setGoogleConsent('accepted')
  writeRecord('accepted')
}

export function rejectAnalyticsConsent() {
  ensureAnalyticsInitialized()
  setGoogleConsent('rejected')
  writeRecord('rejected')
}

export function initializeAnalyticsFromStoredConsent() {
  ensureAnalyticsInitialized()

  const status = getAnalyticsConsentStatus()

  if (status === 'accepted') {
    setGoogleConsent('accepted')
    return
  }

  if (status === 'rejected') {
    setGoogleConsent('rejected')
  }
}
